import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { usePrefersReducedMotion } from '@/hooks/usePrefersReducedMotion'
import {
  useLeaderboards,
  useKrakenState,
  usePastSeasons,
  type LeaderboardBoard,
  type LeaderboardRow,
  type BoardAlignment,
  type PastSeason,
  type PastSeasonBoard,
} from '@/api/hooks'
import { ReputationIcon, RANK_NAMES, type RepFaction } from '@/components/game/ReputationIcon'
import { CaptainPortrait } from '@/components/game/cosmetics/CaptainPortrait'
import { BeastPortrait } from '@/components/game/kraken/BeastPortrait'
import { BeastGlyph } from '@/components/game/kraken/KrakenLayer'
import { LanternIcon,
  GoldIcon,
  PrestigeIcon,
  TrophyIcon,
  SkullIcon,
  BalanceIcon,
  HourglassIcon,
} from '@/components/game/GameIcons'

/** Medal hues per podium place — 1st gold, 2nd silver, 3rd bronze (charte Corsalia). */
const MEDAL = [
  { ring: '#E5CC8F', glow: 'rgba(229,204,143,.40)', label: '1ʳᵉ' },
  { ring: '#BFE4ED', glow: 'rgba(191,228,237,.28)', label: '2ᵉ' },
  { ring: '#C9A55C', glow: 'rgba(201,165,92,.26)', label: '3ᵉ' },
] as const

const FACTION_LABEL: Record<string, string> = {
  guilde: 'Guilde',
  marine: 'Marine',
  corsaire: 'Corsaire',
  pirate: 'Pirate',
  neutral: 'Sans pavillon',
}

/** Per-board icon + headline blurb, keyed by the board's stable key. */
const BOARD_META: Record<
  LeaderboardBoard['key'],
  { Icon: typeof GoldIcon; blurb: string; calm: string }
> = {
  fortune: {
    Icon: GoldIcon,
    blurb: 'Le grand score de la saison : richesse, conquêtes et renommée confondues.',
    calm: 'Trop peu de capitaines ont encore marqué ce tableau. La première fortune de la saison est encore à bâtir.',
  },
  terreur: {
    Icon: SkullIcon,
    blurb: 'Le butin arraché aux autres capitaines en abordage, cette saison.',
    calm: "À peine quelques forbans ont versé le sang cette saison. La chasse vient d'ouvrir : sois le premier à faire trembler l'archipel.",
  },
  negoce: {
    Icon: BalanceIcon,
    blurb: 'Les gains amassés par le commerce honnête et les routes marchandes, cette saison.',
    calm: 'Les routes marchandes restent désertes. Le premier négociant de la saison gravera son nom ici.',
  },
  legendes: {
    Icon: PrestigeIcon,
    blurb: 'Le prestige cumulé de toujours. Seul tableau qui survit aux saisons, un panthéon gravé dans l’or.',
    calm: 'Nul nom n’est encore gravé pour l’éternité. La toute première légende reste à écrire.',
  },
  chantier: {
    Icon: LanternIcon,
    blurb: "Les paliers du Grand Phare érigés cette saison. À nombre égal, le premier à l'avoir dressé passe devant.",
    calm: 'Aucune pierre n’est encore posée. Le premier Grand Phare de la saison reste à allumer.',
  },
}

function fmt(value: number): string {
  return value.toLocaleString('fr-FR').replace(/ /g, ' ')
}

function subline(row: { faction: string; reputationRank: number }): string {
  const fac = FACTION_LABEL[row.faction] ?? FACTION_LABEL.neutral
  if (row.faction === 'neutral' || row.reputationRank <= 0) return 'Sans pavillon'
  const rankName = RANK_NAMES[Math.max(0, Math.min(4, row.reputationRank - 1))]
  return `${fac} · ${rankName}`
}

/** all | loyal | outlaw — the alignment sub-filter of a splittable board. */
type FilterKey = 'all' | BoardAlignment

/** Faction accent hues (mirror ReputationIcon PALETTE) — semantic, distinct from the gold accent. */
const FACTION_HUE: Record<RepFaction, string> = {
  guilde: '#E5CC8F',
  marine: '#6FC6D8',
  corsaire: '#D6A85E',
  pirate: '#E0664B',
}

/**
 * Per-board alignment-filter labels: the faction you EARN by that aligned deed.
 * Terreur rides the warrior axis (Marine ⇄ Pirate); Négoce the merchant axis
 * (Guilde ⇄ Corsaire). Only these two boards carry the sub-filter.
 */
const FILTER_META: Partial<
  Record<
    LeaderboardBoard['key'],
    { loyal: { label: string; faction: RepFaction }; outlaw: { label: string; faction: RepFaction } }
  >
> = {
  terreur: { loyal: { label: 'Marine', faction: 'marine' }, outlaw: { label: 'Pirate', faction: 'pirate' } },
  negoce: { loyal: { label: 'Guilde', faction: 'guilde' }, outlaw: { label: 'Corsaire', faction: 'corsaire' } },
}

/** Small faction emblem for the alignment filter chips (anchor / skull / scales / cutlass). */
function PavillonGlyph({ faction, className }: { faction: RepFaction; className?: string }) {
  const common = {
    className,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.8,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true,
  }
  if (faction === 'marine') {
    return (
      <svg {...common}>
        <circle cx="12" cy="4.4" r="2" />
        <path d="M12 6.4V20M8 10h8M5 13a7 7 0 0 0 14 0M5 13l-1.6.5M5 13l.5-1.8M19 13l1.6.5M19 13l-.5-1.8" />
      </svg>
    )
  }
  if (faction === 'pirate') {
    return (
      <svg {...common}>
        <path d="M12 3.2c3.6 0 6 2.6 6 6 0 1.9-.8 3.1-1.9 4v2.6a1 1 0 0 1-1 1h-.6v2.1h-1.2v-2.1h-1.4v2.1h-1.2v-2.1H9.9a1 1 0 0 1-1-1V13.2C7.8 12.3 7 11.1 7 9.2c0-3.4 2.4-6 5-6Z" />
        <circle cx="9.7" cy="9.9" r="1.3" fill="currentColor" stroke="none" />
        <circle cx="14.3" cy="9.9" r="1.3" fill="currentColor" stroke="none" />
        <path d="M11 13.2l1-1.4 1 1.4" />
      </svg>
    )
  }
  if (faction === 'guilde') {
    return (
      <svg {...common}>
        <path d="M12 4.6V20M8.5 20h7M5 8h14M12 4.9 5 8M12 4.9 19 8" />
        <path d="M2.9 12a2.1 2.1 0 0 0 4.2 0ZM16.9 12a2.1 2.1 0 0 0 4.2 0Z" />
        <path d="M5 8 2.9 12M5 8 7.1 12M19 8 16.9 12M19 8 21.1 12" />
      </svg>
    )
  }
  // corsaire — a curved cutlass
  return (
    <svg {...common}>
      <path d="M4 20C10 18 16 12 20 4" />
      <path d="M4 20l2.2 1.6M4 20l1.6-2.2M4.2 19.2 6.8 21.8" />
    </svg>
  )
}

/**
 * The loyal/outlaw sub-filter for splittable boards, as faction "pavillons":
 * Tout · <loyal faction> · <outlaw faction>, each with its emblem + accent hue.
 */
function AlignmentFilter({
  boardKey,
  align,
  onChange,
}: {
  boardKey: LeaderboardBoard['key']
  align: FilterKey
  onChange: (a: FilterKey) => void
}) {
  const meta = FILTER_META[boardKey]
  if (!meta) return null
  const opts: Array<{ k: FilterKey; label: string; faction: RepFaction | null }> = [
    { k: 'all', label: 'Tout', faction: null },
    { k: 'loyal', label: meta.loyal.label, faction: meta.loyal.faction },
    { k: 'outlaw', label: meta.outlaw.label, faction: meta.outlaw.faction },
  ]
  return (
    <div role="group" aria-label="Filtrer le butin par alignement" className="flex flex-wrap gap-2 mt-3">
      {opts.map((o) => {
        const active = align === o.k
        const hue = o.faction ? FACTION_HUE[o.faction] : '#E5CC8F'
        return (
          <button
            key={o.k}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(o.k)}
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs uppercase tracking-wider transition-colors ${
              active
                ? ''
                : 'border-sand-500/20 bg-ocean-900/40 text-ocean-300 hover:text-sand-200 hover:border-sand-300/40'
            }`}
            style={active ? { color: hue, borderColor: `${hue}66`, background: `${hue}1f` } : undefined}
          >
            {o.faction && <PavillonGlyph faction={o.faction} className="w-4 h-4 shrink-0" />}
            {o.label}
          </button>
        )
      })}
    </div>
  )
}

function MetricValue({
  board,
  value,
  big = false,
}: {
  board: LeaderboardBoard
  value: number
  big?: boolean
}) {
  const numCls = big ? 'text-lg sm:text-2xl' : 'text-base sm:text-lg'
  const icoCls = big ? 'w-4 h-4 sm:w-5 sm:h-5' : 'w-4 h-4'
  return (
    <span className="inline-flex items-center gap-1.5">
      {board.metric === 'gold' && <GoldIcon className={`${icoCls} text-sand-300 shrink-0`} />}
      {board.metric === 'prestige' && <PrestigeIcon className={`${icoCls} text-sand-300 shrink-0`} />}
      <span className={`font-display tabular-nums text-sand-100 leading-none ${numCls}`}>{fmt(value)}</span>
      {board.metric === 'pts' && (
        <span className="text-[10px] uppercase tracking-wider text-ocean-300 self-end mb-0.5 ml-0.5">pts</span>
      )}
      {/* La valeur du board chantier est un NOMBRE DE PALIERS : le serveur trie sur
          un score composé (paliers puis ressources investies) mais ne publie que
          les paliers, seule grandeur qui parle au joueur. */}
      {board.metric === 'paliers' && (
        <span className="text-[10px] uppercase tracking-wider text-ocean-300 self-end mb-0.5 ml-0.5">
          {value > 1 ? 'paliers' : 'palier'}
        </span>
      )}
    </span>
  )
}

/**
 * Grand médaillon d'une ligne de classement : le PORTRAIT peint du capitaine
 * quand il est passé par la cabine, sinon (fallback) son médaillon de faction
 * dans un cadre — c'est l'avatar qui prime désormais (retour utilisateur T18).
 * Le portrait porte son propre anneau, donc pas de cadre carré autour.
 *
 * La taille arrive en CLASSES et non en pixels, pour que le podium puisse
 * rétrécir sous `sm` sans faire redescendre une media query dans le JS : les
 * deux rendus sont des SVG à viewBox, donc une largeur CSS écrase sans peine
 * les attributs width/height, qui ne servent plus que de taille intrinsèque.
 */
function Medallion({ row, size }: { row: LeaderboardRow; size: string }) {
  if (row.appearance?.avatar) {
    return <CaptainPortrait config={row.appearance.avatar} size={92} className={`shrink-0 ${size}`} />
  }
  return (
    <span
      className={`grid place-items-center rounded-xl bg-ocean-900/55 border border-sand-500/20 shrink-0 p-[10%] ${size}`}
    >
      <ReputationIcon
        faction={row.faction}
        rank={row.reputationRank}
        size={92}
        className="w-full h-full"
      />
    </span>
  )
}

/**
 * Fanion de faction accolé au pseudo — montré SEULEMENT quand le grand médaillon
 * porte le portrait peint (sinon la faction est déjà le grand médaillon, inutile
 * de la répéter). L'avatar prime, mais le fanion doit rester bien lisible (32 px,
 * retour utilisateur T18) : c'est l'emblème de réputation du capitaine.
 *
 * Sur le podium mobile il descend à 22 px : la colonne n'y fait plus que ~110 px
 * et un fanion de 32 px y volerait au pseudo la largeur qui lui manque déjà.
 */
function MiniPennant({ row, size = 'w-8 h-8' }: { row: LeaderboardRow; size?: string }) {
  if (!row.appearance?.avatar) return null
  return (
    <span data-mini-pennant aria-hidden="true" className="shrink-0 grid place-items-center">
      <ReputationIcon
        faction={row.faction}
        rank={row.reputationRank}
        size={32}
        className={size}
      />
    </span>
  )
}

function PodiumCard({
  board,
  row,
  place,
  isMe = false,
}: {
  board: LeaderboardBoard
  row: LeaderboardRow
  place: 1 | 2 | 3
  isMe?: boolean
}) {
  const m = MEDAL[place - 1]
  const big = place === 1
  // Médaillons rétrécis sous `sm` : la colonne du podium ne fait qu'une centaine
  // de pixels sur un téléphone (390 px d'écran moins les marges de page, de carte
  // et les gouttières), et un médaillon de 92 px n'y laissait rien au pseudo.
  const medal = big ? 'w-[68px] h-[68px] sm:w-[92px] sm:h-[92px]' : 'w-[52px] h-[52px] sm:w-[72px] sm:h-[72px]'
  // Escalier ancré par le HAUT : sous items-start, le sommet de chaque carte est
  // fixé par un offset top étagé (1ʳᵉ = 0, 2ᵉ, 3ᵉ). Un lift bottom (mb) sous
  // items-end dépendait de la hauteur du contenu (pseudo sur 2 lignes, pastille
  // « Toi »), ce qui pouvait rendre la 3ᵉ carte plus haute que la 2ᵉ sur mobile.
  // Les marches suivent les médaillons : rabotées sur mobile, entières au-delà.
  const drop = place === 2 ? 'mt-6 sm:mt-16' : place === 3 ? 'mt-10 sm:mt-24' : ''
  return (
    <Link
      to={`/capitaine/${encodeURIComponent(row.pseudo)}`}
      className={`group flex flex-col items-center text-center ${drop}`}
    >
      <span className="text-[10px] uppercase tracking-[0.22em] mb-2" style={{ color: m.ring }}>
        {m.label} place
      </span>
      {/* Portrait peint : halo médaille CIRCULAIRE collé à l'anneau du portrait
          (pas de cadre rectangulaire redondant — retour utilisateur T18). Fallback
          faction : cadre arrondi historique autour du médaillon de rang. */}
      <span
        className={`relative inline-grid place-items-center ${row.appearance?.avatar ? 'rounded-full' : 'rounded-2xl'}`}
        style={{
          padding: row.appearance?.avatar ? 0 : 3,
          background: row.appearance?.avatar ? undefined : `linear-gradient(180deg, ${m.ring}, transparent 80%)`,
          boxShadow: `0 0 26px -10px ${m.glow}`,
        }}
      >
        <Medallion row={row} size={medal} />
        <span
          className="absolute -bottom-2.5 left-1/2 -translate-x-1/2 grid place-items-center font-display text-xs rounded-full w-6 h-6 sm:w-7 sm:h-7 border"
          style={{ color: '#0A3E4F', background: m.ring, borderColor: 'rgba(255,255,255,.35)' }}
        >
          {place}
        </span>
      </span>
      <div className="mt-3.5 sm:mt-4 flex flex-col items-center gap-1 w-full">
        {/* line-clamp (display:-webkit-box) vit sur le span interne : le p passe
            en flex pour accoler le mini-portrait de cabine au pseudo.
            Corps rapetissé sous `sm` : un pseudo va jusqu'à 30 caractères SANS
            espace (RegisterUserInput), donc sans point de coupure naturel — à
            15/13 px la plupart tiennent enfin sur une ligne, et les plus longs
            se cassent sur deux au lieu de trois. */}
        <p
          className={`font-display ${big ? 'text-[15px] sm:text-xl' : 'text-[13px] sm:text-base'} text-sand-100 group-hover:text-sand-300 transition-colors leading-tight flex items-center justify-center gap-1 sm:gap-1.5 max-w-full`}
        >
          <MiniPennant row={row} size="w-[22px] h-[22px] sm:w-8 sm:h-8" />
          <span
            className="min-w-0 text-center break-words [overflow-wrap:anywhere] line-clamp-2"
            title={row.appearance?.titleLabel ?? undefined}
          >
            {row.pseudo}
          </span>
        </p>
        {isMe && <MePill />}
      </div>
      <p className="text-[10.5px] uppercase tracking-wider text-ocean-300 mt-1">{subline(row)}</p>
      <div className="mt-2.5">
        <MetricValue board={board} value={row.value} big={big} />
      </div>
    </Link>
  )
}

function Podium({ board }: { board: LeaderboardBoard }) {
  const [first, second, third] = board.rows
  const isMe = (row: LeaderboardRow) => board.me != null && row.place === board.me.place
  // Visual order: 2 · 1 · 3 (centre tallest).
  // Le `-mx-1` mobile fait déborder la grille d'un cran dans le padding de la
  // carte : quatre pixels rendus à chaque colonne, tous pris sur du vide.
  return (
    <div className="grid grid-cols-3 gap-1.5 sm:gap-6 items-start pt-4 pb-6 -mx-1 sm:mx-0">
      <PodiumCard board={board} row={second} place={2} isMe={isMe(second)} />
      <PodiumCard board={board} row={first} place={1} isMe={isMe(first)} />
      <PodiumCard board={board} row={third} place={3} isMe={isMe(third)} />
    </div>
  )
}

/** The small "Toi" badge marking the viewer's own row/podium tile. */
function MePill() {
  return (
    <span className="shrink-0 text-[10px] uppercase tracking-wider text-ocean-900 bg-sand-300 rounded-full px-1.5 py-0.5">
      Toi
    </span>
  )
}

function Row({ board, row, isMe = false }: { board: LeaderboardBoard; row: LeaderboardRow; isMe?: boolean }) {
  return (
    <Link
      to={`/capitaine/${encodeURIComponent(row.pseudo)}`}
      className={`lb-row grid items-center gap-3 px-3 sm:px-4 py-2.5 rounded-xl border transition-colors ${
        isMe
          ? 'bg-sand-300/[0.08] border-sand-300/50'
          : 'border-transparent hover:bg-ocean-700/20 hover:border-sand-300/30'
      }`}
      style={{ gridTemplateColumns: '1.75rem 2.75rem minmax(0,1fr) auto' }}
    >
      <span className="font-display tabular-nums text-ocean-300 text-sm text-center">{row.place}</span>
      <Medallion row={row} size="w-11 h-11" />
      <span className="min-w-0">
        <span className="flex items-center gap-2">
          <MiniPennant row={row} />
          <span className="font-display text-sand-100 truncate" title={row.appearance?.titleLabel ?? undefined}>
            {row.pseudo}
          </span>
          {isMe && <MePill />}
        </span>
        <span className="block text-[11px] uppercase tracking-wider text-ocean-300 truncate mt-0.5">
          {subline(row)}
        </span>
      </span>
      <MetricValue board={board} value={row.value} />
    </Link>
  )
}

function EmptyState({ board }: { board: LeaderboardBoard }) {
  const n = board.rows.length
  return (
    <div className="card relative overflow-hidden rounded-2xl border border-sand-500/20 bg-gradient-to-b from-ocean-700/30 to-[#06222b]/40 p-7 sm:p-9 text-center">
      <div className="pointer-events-none absolute -right-10 -bottom-12 w-56 h-56 opacity-[0.07]" aria-hidden="true">
        <svg viewBox="0 0 200 200" className="compass-rose w-full h-full" fill="none">
          <g stroke="#E5CC8F">
            <circle cx="100" cy="100" r="92" strokeWidth="0.6" />
            <circle cx="100" cy="100" r="60" strokeWidth="0.5" />
          </g>
          {[0, 90, 180, 270].map((deg) => (
            <g key={deg} transform={`rotate(${deg} 100 100)`}>
              <path d="M100,16 L92,90 L100,100 Z" fill="#F5EDD7" />
              <path d="M100,16 L108,90 L100,100 Z" fill="#C9A55C" />
            </g>
          ))}
        </svg>
      </div>
      <div className="relative">
        <TrophyIcon className="w-9 h-9 mx-auto text-sand-300/80" />
        <h3 className="font-display text-2xl text-sand-100 mt-4">Les mers sont encore calmes…</h3>
        <p className="text-sm text-ocean-100 mt-2 max-w-md mx-auto leading-relaxed">{BOARD_META[board.key].calm}</p>
        <p className="text-[11px] uppercase tracking-[0.22em] text-ocean-300 mt-5">
          {n} capitaine{n > 1 ? 's' : ''} déjà au tableau
        </p>
      </div>
    </div>
  )
}

/**
 * A real captain rendered as a centered podium tile in the calm (1–2 captains)
 * state — same medal-ring medallion treatment as the main Podium, placed by
 * standing (1st gold, 2nd silver) so a half-empty board still feels dignified.
 */
function DuoCard({
  board,
  row,
  place,
  lowered = false,
}: {
  board: LeaderboardBoard
  row: LeaderboardRow
  place: 1 | 2
  lowered?: boolean
}) {
  const m = MEDAL[place - 1]
  return (
    <Link
      to={`/capitaine/${encodeURIComponent(row.pseudo)}`}
      className={`group flex flex-col items-center text-center ${lowered ? 'mt-8 sm:mt-16' : ''}`}
    >
      <span className="text-[10px] uppercase tracking-[0.22em] mb-2" style={{ color: m.ring }}>
        {m.label} place
      </span>
      <span
        className={`relative inline-grid place-items-center ${row.appearance?.avatar ? 'rounded-full' : 'rounded-2xl'}`}
        style={{
          padding: row.appearance?.avatar ? 0 : 3,
          background: row.appearance?.avatar ? undefined : `linear-gradient(180deg, ${m.ring}, transparent 80%)`,
          boxShadow: `0 0 26px -10px ${m.glow}`,
        }}
      >
        <Medallion row={row} size="w-[72px] h-[72px]" />
        <span
          className="absolute -bottom-2.5 left-1/2 -translate-x-1/2 grid place-items-center font-display text-xs rounded-full w-7 h-7 border"
          style={{ color: '#0A3E4F', background: m.ring, borderColor: 'rgba(255,255,255,.35)' }}
        >
          {place}
        </span>
      </span>
      <p className="font-display text-base text-sand-100 mt-4 group-hover:text-sand-300 transition-colors leading-tight flex items-center justify-center gap-1.5 max-w-full px-1">
        <MiniPennant row={row} />
        <span
          className="min-w-0 text-center break-words [overflow-wrap:anywhere] line-clamp-2"
          title={row.appearance?.titleLabel ?? undefined}
        >
          {row.pseudo}
        </span>
      </p>
      <p className="text-[10.5px] uppercase tracking-wider text-ocean-300 mt-1">{subline(row)}</p>
      <div className="mt-2.5">
        <MetricValue board={board} value={row.value} />
      </div>
    </Link>
  )
}

/**
 * The calm state for a board with fewer than 3 captains. Real captains (1 or 2)
 * are shown as a centered podium duo ABOVE the dignified calm card — never an
 * empty third slot (proto §7.5).
 */
function CalmBoard({ board }: { board: LeaderboardBoard }) {
  return (
    <div className="space-y-4">
      {board.rows.length > 0 && (
        <div
          className={`grid items-start gap-6 sm:gap-12 mx-auto ${
            board.rows.length === 1 ? 'grid-cols-1 max-w-[10rem]' : 'grid-cols-2 max-w-sm'
          }`}
        >
          {board.rows.map((r, i) => (
            <DuoCard
              key={r.pseudo}
              board={board}
              row={r}
              place={(i + 1) as 1 | 2}
              lowered={board.rows.length === 2 && i === 1}
            />
          ))}
        </div>
      )}
      <EmptyState board={board} />
    </div>
  )
}

function MeBanner({ board }: { board: LeaderboardBoard }) {
  if (!board.me) return null
  return (
    <div className="mt-3 pt-1">
      <div className="flex items-center gap-2 px-4 my-2 text-ocean-300" aria-hidden="true">
        <span className="h-px flex-1 bg-sand-500/15" />
        <span className="text-[10px] tracking-[0.3em]">· · ·</span>
        <span className="h-px flex-1 bg-sand-500/15" />
      </div>
      <div
        className="lb-row grid items-center gap-3 px-3 sm:px-4 py-3 rounded-xl bg-sand-300/[0.08] border border-sand-300/50"
        style={{ gridTemplateColumns: 'auto minmax(0,1fr) auto' }}
      >
        <span className="flex flex-col items-center justify-center px-1">
          <span className="text-[9px] uppercase tracking-[0.2em] text-sand-300">Ta position</span>
          <span className="font-display tabular-nums text-sand-100 text-lg leading-none">#{board.me.place}</span>
        </span>
        <span className="text-sm text-sand-300 font-display truncate">Toi</span>
        <MetricValue board={board} value={board.me.value} />
      </div>
      {board.me.toCatch != null && board.me.toCatch > 0 && (
        <p className="text-xs text-ocean-300 mt-2 px-1">
          Encore <span className="text-sand-300 tabular-nums">{fmt(board.me.toCatch)}</span>
          {board.metric === 'pts' ? ' pts' : ''} pour entrer dans le top&nbsp;10. La marée peut tourner.
        </p>
      )}
    </div>
  )
}

function BoardView({ board }: { board: LeaderboardBoard }) {
  const [align, setAlign] = useState<FilterKey>('all')
  const hasFilter = board.alignments != null && FILTER_META[board.key] != null

  // The displayed ranking follows the selected pavillon: "Tout" reuses the
  // board's own rows/me; loyal/outlaw swap in that variant's sub-ranking.
  const view: LeaderboardBoard =
    hasFilter && align !== 'all' && board.alignments != null
      ? { ...board, rows: board.alignments[align].rows, me: board.alignments[align].me }
      : board

  const meInTop = view.me != null && view.me.place <= view.rows.length && view.me.place <= 10
  const calm = view.rows.length < 3

  return (
    <section
      role="tabpanel"
      id="lb-panel"
      aria-labelledby={`tab-${board.key}`}
      tabIndex={0}
      className={`card rounded-2xl border bg-gradient-to-b from-ocean-700/30 to-[#06222b]/40 p-3 sm:p-6 ${
        board.eternal ? 'eternal-frame border-sand-300/40' : 'border-sand-500/20'
      }`}
    >
      <div className="flex items-start sm:items-center justify-between gap-4 flex-wrap mb-1">
        <div>
          <h2 className="font-display text-xl sm:text-2xl text-sand-100 flex items-center gap-2.5">
            {(() => {
              const Icon = BOARD_META[board.key].Icon
              return <Icon className="w-6 h-6 text-sand-300" />
            })()}
            {board.label}
          </h2>
          <p className="text-sm text-ocean-100 mt-1 max-w-lg">{BOARD_META[board.key].blurb}</p>
        </div>
        {board.eternal ? (
          <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] px-3 py-1.5 rounded-full border border-sand-300/45 text-sand-300 bg-sand-300/10">
            <PrestigeIcon className="w-3.5 h-3.5 text-sand-300" />
            Éternel · toutes saisons
          </span>
        ) : (
          <span className="text-[11px] uppercase tracking-[0.2em] text-ocean-300 hidden sm:inline">Saison</span>
        )}
      </div>

      {hasFilter && <AlignmentFilter boardKey={board.key} align={align} onChange={setAlign} />}

      <div className="mt-4">
        {calm ? (
          <CalmBoard board={view} />
        ) : (
          <>
            <Podium board={view} />
            <div className="space-y-1.5 pt-1">
              {view.rows.slice(3, 10).map((r) => (
                <Row
                  key={r.pseudo}
                  board={view}
                  row={r}
                  isMe={view.me != null && r.place === view.me.place}
                />
              ))}
            </div>
            {view.me && !meInTop && <MeBanner board={view} />}
          </>
        )}
      </div>
    </section>
  )
}

/**
 * Le rail qui porte la barre d'onglets. Les sept onglets actuels tiennent
 * d'eux-mêmes sur un écran de 360 px (7 × 38 px de large), donc en pratique
 * rien ne défile jamais : le rail est le filet du jour où un huitième tableau
 * arrivera. Sans lui, la barre centrée débordait des deux bords de l'écran et
 * le premier comme le dernier onglet devenaient intouchables (saison 2, sept
 * onglets pour 358 px utiles).
 *
 * Les deux fondus de bord ne s'allument que du côté où il reste de la route :
 * une barre qui tient entièrement n'en montre aucun.
 */
function TabRail({ activeIndex, children }: { activeIndex: number; children: React.ReactNode }) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [edges, setEdges] = useState({ start: true, end: true })
  const reducedMotion = usePrefersReducedMotion()

  const syncEdges = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    const max = el.scrollWidth - el.clientWidth
    setEdges({ start: el.scrollLeft <= 1, end: el.scrollLeft >= max - 1 })
  }, [])

  useEffect(() => {
    syncEdges()
    const el = scrollRef.current
    if (!el || typeof ResizeObserver === 'undefined') return
    // La largeur du rail bouge sans scroll : rotation de l'écran, et surtout
    // l'onglet « Saisons passées » qui apparaît quand son archive arrive.
    const ro = new ResizeObserver(syncEdges)
    ro.observe(el)
    return () => ro.disconnect()
  }, [syncEdges])

  // L'onglet actif revient toujours dans le champ — au clavier comme au doigt.
  // `block: 'nearest'` garde la page verticalement immobile quand la barre est
  // déjà visible, ce qui est le cas normal.
  useEffect(() => {
    const tab = scrollRef.current?.querySelectorAll<HTMLElement>('[role="tab"]')[activeIndex]
    tab?.scrollIntoView?.({
      block: 'nearest',
      inline: 'center',
      behavior: reducedMotion ? 'auto' : 'smooth',
    })
  }, [activeIndex, reducedMotion])

  return (
    <div
      className="lb-rail relative"
      data-at-start={edges.start ? 'true' : undefined}
      data-at-end={edges.end ? 'true' : undefined}
    >
      {/* Le trait de soulignement reste porté par la liste elle-même : les
          onglets le mordent d'un pixel (`-mb-px`), et ce pixel déborderait
          verticalement d'un conteneur en `overflow-x: auto` — lequel calcule
          alors son `overflow-y` en `auto` et rognerait la bordure basse. */}
      <div ref={scrollRef} data-lb-rail-scroll onScroll={syncEdges} className="lb-rail-scroll overflow-x-auto">
        <div className="flex justify-center min-w-max px-1">{children}</div>
      </div>
    </div>
  )
}

export function Leaderboards() {
  const { data, isLoading, isError } = useLeaderboards()
  // Le palmarès est lu ICI et non dans son panneau, parce qu'il décide de la
  // PRÉSENCE de son onglet : un onglet qui ne mènerait qu'à « rien à lire »
  // n'a pas lieu d'être. Des deux requêtes c'est de loin la plus légère (une
  // archive figée, une seule requête jointe, gardée fraîche 5 min et partagée
  // avec le livret de bord), et elle part en parallèle des classements : en
  // pratique elle est revenue la première et la barre d'onglets ne bouge pas
  // sous le curseur. Si elle échoue, l'onglet reste absent et le reste de la
  // page vit sa vie.
  const past = usePastSeasons()
  const [active, setActive] = useState(0)
  const tablistRef = useRef<HTMLDivElement>(null)

  if (isLoading) {
    return (
      <section className="max-w-4xl mx-auto p-4 sm:p-6">
        <p className="text-ocean-100">Chargement…</p>
      </section>
    )
  }
  if (isError || !data || data.boards.length === 0) {
    return (
      <section className="max-w-4xl mx-auto p-4 sm:p-6">
        <p className="text-rum-500">Impossible de charger les classements.</p>
      </section>
    )
  }

  const boards = data.boards
  // Onglet SYNTHÉTIQUE, en dernier : le tableau de chasse n'est pas un classement
  // (l'ordre est chronologique, il n'y a rien à départager) et ne vient donc pas
  // de l'API des classements. Il vit ici parce que c'est là qu'on vient lire les
  // faits d'armes de la saison, à côté du panthéon éternel.
  const huntTabIndex = boards.length
  // Onglet SYNTHÉTIQUE lui aussi, tout au bout : le palmarès n'est pas un
  // classement de plus, c'est la mémoire des saisons closes. Il n'apparaît qu'à
  // partir de la première clôture : avant, il n'y a rien de gravé à lire.
  const pastSeasons = past.data?.seasons ?? []
  const hasPastSeasons = pastSeasons.length > 0
  const pastTabIndex = boards.length + 1
  const tabCount = boards.length + 1 + (hasPastSeasons ? 1 : 0)
  // Le compte d'onglets n'est plus une constante : il grandit d'un cran quand
  // l'archive arrive. On ne laisse jamais `active` désigner un onglet disparu,
  // sinon `boards[active]` rendrait undefined et le panneau planterait.
  const activeIndex = active < tabCount ? active : 0
  const huntActive = activeIndex === huntTabIndex
  const pastActive = activeIndex === pastTabIndex
  const board = boards[activeIndex]

  function onTablistKey(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return
    e.preventDefault()
    const dir = e.key === 'ArrowRight' ? 1 : -1
    const next = (activeIndex + dir + tabCount) % tabCount
    setActive(next)
    const btn = tablistRef.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]')[next]
    btn?.focus()
  }

  return (
    <>
      {/* Hall-of-fame backdrop — grain + a slow compass rose (reduced-motion safe). */}
      <style>{`
        @keyframes lbSlowSpin { from { transform: rotate(0) } to { transform: rotate(360deg) } }
        .compass-rose { animation: lbSlowSpin 220s linear infinite; transform-origin: center; }
        .eternal-frame { box-shadow: inset 0 0 0 1px rgba(229,204,143,.10), 0 0 60px -28px rgba(229,204,143,.5); }
        .lb-row { transition: background .2s, border-color .2s; }
        /* Le rail d'onglets : barre de défilement masquée (les fondus de bord
           disent déjà qu'il reste de la route), fondus peints dans la couleur
           du fond de page (body.bg-ocean-900). */
        .lb-rail-scroll { scrollbar-width: none; -ms-overflow-style: none; }
        .lb-rail-scroll::-webkit-scrollbar { display: none; }
        .lb-rail::before, .lb-rail::after {
          content: ""; position: absolute; top: 0; bottom: 0; width: 2rem;
          pointer-events: none; z-index: 1; transition: opacity .2s;
        }
        .lb-rail::before { left: 0; background: linear-gradient(90deg, #0A3E4F, rgba(10,62,79,0)); }
        .lb-rail::after { right: 0; background: linear-gradient(270deg, #0A3E4F, rgba(10,62,79,0)); }
        .lb-rail[data-at-start]::before, .lb-rail[data-at-end]::after { opacity: 0; }
        @media (prefers-reduced-motion: reduce) {
          .compass-rose { animation: none !important; }
          .lb-rail::before, .lb-rail::after { transition: none; }
        }
      `}</style>

      <section className="max-w-4xl mx-auto px-4 sm:px-6 py-9 pb-20 space-y-7">
        <header className="text-center max-w-2xl mx-auto">
          <h1 className="font-display text-4xl sm:text-5xl text-sand-300 leading-none">Classements</h1>
          {/* La saison courante, servie par l'API depuis toujours et lue par
              personne jusqu'ici : c'est ici qu'on vient demander quand les
              tableaux se figent (fiche 31, lot 31f). Rendue en un seul nœud de
              texte, pour que la ligne se lise d'un bloc. */}
          <p className="mt-2 text-[11px] uppercase tracking-[0.22em] text-ocean-300 tabular-nums">
            {`${data.seasonName} · semaine ${data.week} / ${data.totalWeeks}`}
          </p>
        </header>

        {/* Single-row icon strip — the active board's full name lives in the
            panel header right below, so the tabs stay slim and the podium rides
            up. Icon-only tabs carry their name via aria-label/title.
            Les onglets sont resserrés sous `sm` (px-2, icône 22 px) pour que les
            sept tiennent d'un bloc dès 360 px de large, et le rail les rattrape
            au-delà. */}
        <TabRail activeIndex={activeIndex}>
          <div
            ref={tablistRef}
            role="tablist"
            aria-label="Tableaux de classement"
            onKeyDown={onTablistKey}
            className="inline-flex items-stretch border-b border-sand-500/20"
          >
            {boards.map((b, i) => {
              const selected = i === activeIndex
              const Icon = BOARD_META[b.key].Icon
              return (
                <button
                  key={b.key}
                  role="tab"
                  id={`tab-${b.key}`}
                  aria-controls="lb-panel"
                  aria-selected={selected}
                  aria-label={b.label}
                  title={b.label}
                  tabIndex={selected ? 0 : -1}
                  onClick={() => setActive(i)}
                  className={`relative -mb-px flex items-center justify-center px-2 sm:px-9 py-3 border-b-2 transition-colors rounded-t-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-sand-300 ${
                    selected
                      ? 'border-sand-300 text-sand-300'
                      : 'border-transparent text-ocean-300 hover:text-sand-200'
                  }`}
                >
                  <Icon className="w-[22px] h-[22px] sm:w-6 sm:h-6" />
                </button>
              )
            })}
            <button
              role="tab"
              id="tab-chasses"
              aria-controls="lb-panel"
              aria-selected={huntActive}
              aria-label="Tableau de chasse"
              title="Tableau de chasse"
              tabIndex={huntActive ? 0 : -1}
              onClick={() => setActive(huntTabIndex)}
              className={`relative -mb-px flex items-center justify-center px-2 sm:px-9 py-3 border-b-2 transition-colors rounded-t-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-sand-300 ${
                huntActive ? 'border-sand-300 text-sand-300' : 'border-transparent text-ocean-300 hover:text-sand-200'
              }`}
            >
              <BeastGlyph className="w-[22px] h-[22px] sm:w-6 sm:h-6" />
            </button>
            {hasPastSeasons && (
              <button
                role="tab"
                id="tab-saisons-passees"
                aria-controls="lb-panel"
                aria-selected={pastActive}
                aria-label="Saisons passées"
                title="Saisons passées"
                tabIndex={pastActive ? 0 : -1}
                onClick={() => setActive(pastTabIndex)}
                className={`relative -mb-px flex items-center justify-center px-2 sm:px-9 py-3 border-b-2 transition-colors rounded-t-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-sand-300 ${
                  pastActive ? 'border-sand-300 text-sand-300' : 'border-transparent text-ocean-300 hover:text-sand-200'
                }`}
              >
                <HourglassIcon className="w-[22px] h-[22px] sm:w-6 sm:h-6" />
              </button>
            )}
          </div>
        </TabRail>

        {pastActive ? (
          <PastSeasonsView
            seasons={pastSeasons}
            seasonal={boards.filter((b) => !b.eternal)}
            currentSeasonName={data.seasonName}
          />
        ) : huntActive ? (
          <HuntBoardView />
        ) : (
          <BoardView key={board.key} board={board} />
        )}

        <p className="text-center text-xs text-ocean-300 pt-6">
          Les classements se réinitialisent à chaque saison, sauf les{' '}
          <span className="text-sand-300">Légendes</span>, gravées pour toujours.
          {hasPastSeasons && (
            <>
              {' '}
              Le podium des saisons closes, lui, reste consultable dans l'onglet{' '}
              <span className="text-sand-300">Saisons passées</span>.
            </>
          )}
        </p>
      </section>
    </>
  )
}

/**
 * Le tableau de chasse de la saison (fiche 32) : la bête qui remonte, puis les
 * capitaines qui en sont venus à bout.
 *
 * ⚠️ Copie sans accord de genre : le catalogue mêle un masculin (Le Kraken) et un
 * féminin (L'Armada fantôme), et un « abattue » collé sous « Le Kraken » saute
 * aux yeux. Soit « la bête » est le sujet explicite, soit la tournure est neutre.
 *
 * Ce n'est pas un classement et il ne doit pas en avoir l'air : ni podium, ni
 * médaille, ni rang. L'ordre est celui des dates, parce qu'abattre la bête est
 * un fait, pas une performance à départager.
 */
function HuntBoardView() {
  const { data } = useKrakenState()

  return (
    <section id="lb-panel" role="tabpanel" aria-labelledby="tab-chasses" className="space-y-5">
      <header className="flex items-center justify-center gap-4">
        <BeastPortrait archetypeKey={data?.archetype.key ?? 'kraken'} size={72} />
        <div>
          <p className="text-[11px] uppercase tracking-[0.18em] text-ocean-300">La bête de cette saison</p>
          <h2 className="font-display text-2xl text-sand-300 leading-tight">{data?.archetype.label ?? '—'}</h2>
          {data?.archetype.titleLabel && (
            <p className="text-xs text-ocean-200">
              Titre en jeu : <span className="text-sand-200">« {data.archetype.titleLabel} »</span>
            </p>
          )}
        </div>
      </header>

      {data && data.board.length > 0 ? (
        <ul className="divide-y divide-sand-500/10 rounded-2xl border border-sand-500/20 bg-ocean-900/40">
          {data.board.map((entry, i) => (
            <li key={`${entry.pseudo ?? 'x'}-${i}`} className="flex items-center justify-between gap-3 px-4 py-3">
              <span>
                <span className="font-display text-sand-200">{entry.pseudo ?? 'Un capitaine'}</span>
                <span className="block text-[11px] text-ocean-300">
                  Vainqueur en {entry.engagements} tentative{entry.engagements > 1 ? 's' : ''}
                </span>
              </span>
              <span className="text-xs tabular-nums text-ocean-300">
                {entry.felledAt
                  ? new Date(entry.felledAt).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })
                  : ''}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="rounded-2xl border border-dashed border-sand-500/25 bg-ocean-900/30 px-4 py-8 text-center text-sm text-ocean-300">
          Personne n'en est encore venu à bout.
        </p>
      )}

      <p className="text-center text-xs text-ocean-300">
        Le titre reste gravé sur le compte de son vainqueur ; la chasse, elle, s'efface au reset.
      </p>
    </section>
  )
}

/** Valeur d'une ligne de palmarès : même grammaire d'icônes que les classements vivants. */
function PastMetric({ metric, value }: { metric: PastSeasonBoard['metric']; value: number }) {
  return (
    <span className="inline-flex items-baseline gap-1">
      {metric === 'gold' && <GoldIcon className="w-3.5 h-3.5 text-sand-300 shrink-0 self-center" />}
      {metric === 'prestige' && <PrestigeIcon className="w-3.5 h-3.5 text-sand-300 shrink-0 self-center" />}
      <span className="font-display tabular-nums text-sand-200 text-sm leading-none">{fmt(value)}</span>
      {metric === 'pts' && <span className="text-[10px] uppercase tracking-wider text-ocean-300">pts</span>}
      {metric === 'paliers' && (
        <span className="text-[10px] uppercase tracking-wider text-ocean-300">{value > 1 ? 'paliers' : 'palier'}</span>
      )}
    </span>
  )
}

/**
 * Une place du podium gravé. `row` à null = place VACANTE : elle le dit
 * (« personne ») au lieu de laisser une ligne vide ou d'inventer un capitaine.
 * Le relevé de prod montre qu'un board peut n'avoir aucun premier (le Grand
 * Chantier, quand nul n'a posé de pierre) : les trois places existent donc
 * toujours, et se taisent quand il n'y a personne à sacrer.
 */
function PastPlace({
  rank,
  metric,
  row,
}: {
  rank: number
  metric: PastSeasonBoard['metric']
  row: { pseudo: string; score: number } | null
}) {
  const medal = MEDAL[rank - 1]
  return (
    <li className="flex items-start gap-2">
      <span
        className={`grid place-items-center font-display tabular-nums text-[11px] rounded-full w-5 h-5 shrink-0 mt-0.5 ${
          row ? '' : 'bg-ocean-900/60 border border-sand-500/20 text-ocean-300'
        }`}
        style={row ? { color: '#0A3E4F', background: medal?.ring ?? '#C9A55C' } : undefined}
      >
        {rank}
      </span>
      {row ? (
        <span className="min-w-0">
          <Link
            to={`/capitaine/${encodeURIComponent(row.pseudo)}`}
            className="block truncate text-sm text-sand-100 hover:text-sand-300 transition-colors"
          >
            {row.pseudo}
          </Link>
          <PastMetric metric={metric} value={row.score} />
        </span>
      ) : (
        <span className="text-sm text-ocean-300 italic">personne</span>
      )}
    </li>
  )
}

/**
 * Le bloc d'UNE saison close : son nom, ses dates, et ses quatre podiums côte à
 * côte. La saison est l'unité de lecture, pas le tableau : on vient voir « qui a
 * gagné la Saison des Abysses », pas « qui a gagné le Négoce en général ».
 *
 * Les quatre tableaux saisonniers viennent de la liste VIVANTE des classements
 * (`seasonal`), pas du palmarès archivé : celui-ci ne contient que les boards
 * réellement marqués, et un tableau absent doit tout de même occuper sa colonne
 * pour dire que personne ne l'a marqué cette saison-là.
 */
function SeasonPalmares({ season, seasonal }: { season: PastSeason; seasonal: LeaderboardBoard[] }) {
  const dates = `du ${new Date(season.startedAt).toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'short',
  })} au ${new Date(season.endsAt).toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })}`

  return (
    <article className="card rounded-2xl border border-sand-500/20 bg-gradient-to-b from-ocean-700/30 to-[#06222b]/40 p-4 sm:p-6">
      <header className="flex items-baseline justify-between gap-4 flex-wrap">
        <h3 className="font-display text-xl text-sand-100">{season.name}</h3>
        <p className="text-[11px] uppercase tracking-[0.18em] text-ocean-300 tabular-nums">{dates}</p>
      </header>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 mt-4">
        {seasonal.map((meta) => {
          const archived = season.boards.find((b) => b.key === meta.key)
          const Icon = BOARD_META[meta.key].Icon
          const metric = archived?.metric ?? meta.metric
          return (
            <div
              key={meta.key}
              data-podium={meta.key}
              className="rounded-xl border border-sand-500/15 bg-ocean-900/40 p-3.5"
            >
              <p className="font-display text-sm text-sand-200 flex items-center gap-2 leading-tight">
                <Icon className="w-4 h-4 text-sand-300 shrink-0" />
                {archived?.label ?? meta.label}
              </p>
              <ol className="mt-3 space-y-2.5">
                {[1, 2, 3].map((rank) => (
                  <PastPlace
                    key={rank}
                    rank={rank}
                    metric={metric}
                    row={archived?.rows.find((r) => r.rank === rank) ?? null}
                  />
                ))}
              </ol>
            </div>
          )
        })}
      </div>

      <p className="text-xs text-ocean-300 mt-4">
        Les trois premiers de chaque tableau ont reçu un titre de podium à la clôture, à équiper dans la cabine du
        capitaine.
      </p>
    </article>
  )
}

/**
 * Le palmarès des saisons closes. Ce n'est PAS un classement et il ne doit pas
 * en avoir l'air : ni podium en escalier, ni pastille « Toi », ni sous-filtre de
 * pavillon, ni écart à rattraper. Rien n'y bouge plus, c'est une gravure.
 *
 * `seasons` arrive du parent et n'est JAMAIS vide : c'est lui qui décide si
 * l'onglet existe, et il ne l'ouvre qu'à partir de la première clôture. Le
 * panneau n'a donc ni chargement ni état vide à porter, et ne peut pas se
 * retrouver à annoncer un palmarès qu'il n'a pas.
 *
 * La saison en cours ouvre quand même le panneau, non pas avec un podium
 * provisoire, mais avec la promesse de sa clôture.
 */
function PastSeasonsView({
  seasons,
  seasonal,
  currentSeasonName,
}: {
  seasons: PastSeason[]
  seasonal: LeaderboardBoard[]
  currentSeasonName: string
}) {
  return (
    <section id="lb-panel" role="tabpanel" aria-labelledby="tab-saisons-passees" tabIndex={0} className="space-y-6">
      <header className="text-center">
        <h2 className="font-display text-2xl text-sand-300">Saisons passées</h2>
        <p className="text-sm text-ocean-100 mt-1.5 max-w-lg mx-auto">
          Le podium de chaque saison close, gravé au moment du couperet. Les tables de jeu repartent de zéro ; ces
          noms, eux, restent.
        </p>
      </header>

      {/* La saison en cours : pas de podium à montrer, mais une promesse à tenir. */}
      <div className="rounded-2xl border border-dashed border-sand-300/30 bg-ocean-900/30 px-4 py-4 sm:px-6">
        <div className="flex items-baseline justify-between gap-4 flex-wrap">
          <h3 className="font-display text-lg text-sand-200">{currentSeasonName}</h3>
          <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-sand-300">
            <HourglassIcon className="w-3.5 h-3.5" />
            Saison en cours
          </span>
        </div>
        <p className="text-sm text-ocean-100 mt-1.5">
          Son podium se gravera à la clôture, quand les tables repartiront de zéro. D'ici là, tout peut encore
          changer : le classement vivant est dans les autres onglets.
        </p>
      </div>

      {seasons.map((s) => (
        <SeasonPalmares key={s.id} season={s} seasonal={seasonal} />
      ))}
    </section>
  )
}

export default Leaderboards
